import { Controller } from "@hotwired/stimulus"
import { loadMapLibre, loadBaseStyle, escapeHtml, createPin, directionsUrl } from "../lib/maplibre"
import { LocateControl } from "../lib/user_location"

// Keep pins clear of the key and filter chips (top left) and the map controls (top right)
const FIT_PADDING = { top: 72, bottom: 40, left: 40, right: 64 }
const MAX_FIT_ZOOM = 15
// Pins this far from the middle of the guide are almost certainly mistyped coordinates
const OUTLIER_DISTANCE_KM = 100
// How often pins check whether their events have started or finished
const LIVE_CHECK_MS = 60 * 1000

export default class extends Controller {
  static targets = [
    "container", "status", "overlay",
    "sheet", "sheetTitle", "sheetBody"
  ]
  static values = {
    eventsUrl: String,
    centerLat: Number,
    centerLng: Number,
    weekSlug: String
  }

  connect() {
    this.generation = (this.generation || 0) + 1
    this.groups = []
    this.selectedGroup = null

    // Turbo's cached page snapshots can include the previous map and an open sheet
    this.containerTarget.replaceChildren()
    this.sheetTarget.hidden = true
    this.handleKeydown = (event) => {
      if (event.key === "Escape") this.closeSheet()
    }
    document.addEventListener("keydown", this.handleKeydown)
    // A phone can bring back a map left open for hours, so check again on coming back to it
    this.handleVisibility = () => {
      if (document.visibilityState === "visible") this.refreshLive()
    }
    document.addEventListener("visibilitychange", this.handleVisibility)
    this.liveTimer = setInterval(() => this.refreshLive(), LIVE_CHECK_MS)
    this.load(this.generation)
  }

  disconnect() {
    this.generation++
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("visibilitychange", this.handleVisibility)
    clearInterval(this.liveTimer)
    this.map?.remove()
    this.map = null
  }

  async load(generation) {
    this.showStatus("loading")
    const eventsRequest = this.fetchEvents()

    let maplibregl, style
    try {
      [maplibregl, style] = await Promise.all([loadMapLibre(), loadBaseStyle()])
    } catch (error) {
      console.error("Failed to load map:", error)
      if (generation === this.generation) this.showStatus("error")
      return
    }
    if (generation !== this.generation) return

    try {
      this.initializeMap(maplibregl, style)
    } catch (error) {
      // e.g. WebGL is unavailable on this device
      console.error("Failed to initialize map:", error)
      this.showStatus("error")
      return
    }

    const events = await eventsRequest
    if (generation !== this.generation) return

    this.renderEvents(maplibregl, events)
  }

  initializeMap(maplibregl, style) {
    const hasCenter = Boolean(this.centerLatValue || this.centerLngValue)

    this.map = new maplibregl.Map({
      container: this.containerTarget,
      style,
      center: [this.centerLngValue || 0, this.centerLatValue || 0],
      zoom: hasCenter ? 12 : 1,
      maxZoom: 19,
      // The page draws the map's credits itself (guide_map), so they show before the map has loaded
      attributionControl: false,
      // Keep north up: rotating by accident on a phone is disorienting
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false
    })
    this.map.touchZoomRotate.disableRotation()
    this.map.keyboard.disableRotation()

    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")
    this.map.addControl(new LocateControl(maplibregl), "top-right")

    this.map.on("load", () => {
      if (this.status === "loading") this.showStatus(null)
    })
    this.map.on("click", (event) => {
      if (event.originalEvent?.target?.closest?.(".map-pin")) return
      this.closeSheet()
    })
  }

  async fetchEvents() {
    try {
      const response = await fetch(this.eventsUrlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const events = await response.json()

      if (!this.isFilteringFavourites()) return events

      const favourites = this.getFavourites()
      return events.filter(event => favourites.includes(event.id))
    } catch (error) {
      console.error("Failed to load events:", error)
      return null
    }
  }

  renderEvents(maplibregl, events) {
    if (!events) {
      this.showStatus("error")
      return
    }

    this.groups = groupByLocation(events)

    if (this.groups.length === 0) {
      this.showStatus("empty")
      return
    }

    this.groups.forEach(group => {
      const first = group.events[0]
      const count = group.events.length

      group.label = count > 1 ? `${count} events at ${group.title}` : first.title
      group.pin = createPin({
        color: first.type_color,
        icon: first.type_icon,
        count,
        label: group.label
      })
      group.pin.addEventListener("click", (event) => {
        event.stopPropagation()
        this.select(group)
      })

      group.marker = new maplibregl.Marker({ element: group.pin, anchor: "bottom" })
        .setLngLat(group.lngLat)
        .addTo(this.map)
    })

    // Frame the pins clustered around the guide so one mistyped coordinate can't zoom
    // the map out to half the world (far-off pins stay on the map, reachable by panning)
    this.framedGroups = withoutOutliers(this.groups)
    this.bounds = new maplibregl.LngLatBounds()
    this.framedGroups.forEach(group => this.bounds.extend(group.lngLat))
    this.refreshLive()

    this.map.addControl(new FitEventsControl(() => this.fitToEvents()), "top-right")
    this.fitToEvents({ animate: false })
  }

  fitToEvents({ animate = true } = {}) {
    if (!this.map || !this.bounds) return

    if (this.framedGroups.length === 1) {
      this.map.easeTo({ center: this.framedGroups[0].lngLat, zoom: MAX_FIT_ZOOM, animate })
    } else {
      this.map.fitBounds(this.bounds, { padding: this.fitPadding(), maxZoom: MAX_FIT_ZOOM, animate })
    }
  }

  // The filter chips can wrap onto a second row on phones
  fitPadding() {
    if (!this.hasOverlayTarget) return FIT_PADDING

    const below = this.overlayTarget.getBoundingClientRect().bottom - this.element.getBoundingClientRect().top
    return { ...FIT_PADDING, top: Math.max(FIT_PADDING.top, Math.round(below) + 24) }
  }

  // Live now: a pin pulses while any of its events is on

  refreshLive() {
    const now = Date.now()
    this.groups.forEach(group => {
      if (!group.pin) return

      const live = group.events.some(event => isLive(event, now))
      group.pin.classList.toggle("is-live", live)
      group.pin.setAttribute("aria-label", live ? `${group.label}, on now` : group.label)
    })
  }

  // Selected event sheet

  select(group) {
    this.selectedGroup?.pin.classList.remove("is-selected")
    this.selectedGroup = group
    group.pin.classList.add("is-selected")

    const count = group.events.length
    this.sheetTitleTarget.textContent = count > 1 ? `${group.title} · ${count} events` : group.title

    // What's on now first, then by time
    const now = Date.now()
    const events = [ ...group.events ].sort((a, b) => isLive(b, now) - isLive(a, now))
    const favourites = this.getFavourites()
    this.sheetBodyTarget.innerHTML = events.map(event => this.eventCard(event, favourites, now)).join("")
    this.sheetBodyTarget.scrollTop = 0
    this.sheetTarget.hidden = false

    this.revealSelected(group)
  }

  closeSheet() {
    if (!this.hasSheetTarget || this.sheetTarget.hidden) return

    this.sheetTarget.hidden = true
    this.selectedGroup?.pin.classList.remove("is-selected")
    this.selectedGroup = null
  }

  // Pan only when the pin would end up hidden behind the sheet or off screen
  revealSelected(group) {
    const point = this.map.project(group.lngLat)
    const width = this.containerTarget.clientWidth
    const height = this.containerTarget.clientHeight
    const sheetTop = this.sheetTarget.offsetTop

    const visible = point.x > 32 && point.x < width - 32 && point.y > 56 && point.y < sheetTop - 8
    if (visible) return

    const visibleHeight = Math.min(sheetTop, height)
    this.map.easeTo({
      center: group.lngLat,
      offset: [0, (visibleHeight - height) / 2 + 20],
      duration: 400
    })
  }

  eventCard(event, favourites, now) {
    const isFav = favourites.includes(event.id)
    const location = event.venue_name || event.address || event.neighborhood
    const title = escapeHtml(event.title)
    const analytics = (name) => `data-sa-event="${name}" data-sa-meta-event="${escapeHtml(event.slug)}" data-sa-meta-event-id="${event.id}" data-sa-meta-source="map" data-sa-guide="${escapeHtml(this.weekSlugValue)}"`

    let html = '<article class="flex gap-3 px-4 py-3.5">'

    if (event.featured_image_url) {
      html += `<img src="${escapeHtml(event.featured_image_url)}" alt="" loading="lazy" class="size-16 shrink-0 rounded-[0.625rem] object-cover bg-stone-100" />`
    }

    html += '<div class="min-w-0 flex-1">'
    html += '<div class="flex items-start justify-between gap-2">'
    html += '<div class="min-w-0">'
    html += `<p class="flex items-center gap-1.5 text-caption font-medium text-stone-500"><span class="size-[7px] shrink-0 rounded-full" style="background-color: ${escapeHtml(event.type_color)}"></span>${escapeHtml(event.type_label || "Event")}</p>`
    html += event.details_path
      ? `<h3 class="mt-0.5 line-clamp-2 text-headline text-stone-900"><a href="${escapeHtml(event.details_path)}" data-count-view="${escapeHtml(event.count_view_path)}" ${analytics("event_open")} class="hover:underline underline-offset-2">${title}</a></h3>`
      : `<h3 class="mt-0.5 line-clamp-2 text-headline text-stone-900">${title}</h3>`
    html += '</div>'
    html += `
      <button
        type="button"
        data-action="click->favourites#toggle"
        data-event-id="${event.id}"
        data-favourites-target="heart"
        class="-mr-2.5 -mt-2.5 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full ${isFav ? "text-red-500" : "text-stone-400"} transition-colors hover:text-red-500"
        aria-label="Add to favourites"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="${isFav ? "currentColor" : "none"}" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" class="size-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
        </svg>
      </button>
    `
    html += '</div>'

    const when = [event.time_label, event.date_label].filter(Boolean).map(escapeHtml)
    if (isLive(event, now)) {
      html += '<p class="mt-1 flex items-center gap-1.5 truncate text-footnote">'
      html += '<span class="relative flex size-1.5 shrink-0" aria-hidden="true"><span class="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none"></span><span class="relative inline-flex size-1.5 rounded-full bg-emerald-500"></span></span>'
      html += `<span class="font-semibold text-emerald-700">Live</span><span class="text-stone-300">·</span><span class="text-stone-500">until ${escapeHtml(event.live_until_label)}</span>`
      html += "</p>"
    } else if (when.length) {
      html += `<p class="mt-1 truncate text-footnote"><span class="font-medium text-stone-700">${when[0]}</span>`
      if (when[1]) html += `<span class="mx-1 text-stone-300">·</span><span class="text-stone-500">${when[1]}</span>`
      html += "</p>"
    }
    if (location) {
      html += `<p class="truncate text-footnote text-stone-500">${escapeHtml(location)}</p>`
    }

    html += '<div class="mt-2.5 flex flex-wrap items-center gap-1.5">'
    if (event.details_path) {
      html += `<a href="${escapeHtml(event.details_path)}" data-count-view="${escapeHtml(event.count_view_path)}" ${analytics("event_open")} class="btn btn-sm btn-secondary">Details</a>`
    }
    if (event.rsvp_path) {
      html += `<a href="${escapeHtml(event.rsvp_path)}" target="_blank" rel="noopener" ${analytics("rsvp_click")} class="btn btn-sm btn-primary">RSVP <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.25" stroke="currentColor" class="size-3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" /></svg></a>`
    }
    html += `
      <a href="${escapeHtml(directionsUrl(event.lat, event.lng))}" target="_blank" rel="noopener" class="btn btn-sm btn-ghost" aria-label="Directions to ${title}">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-3.5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
        </svg>
        Directions
      </a>
    `
    html += "</div></div></article>"

    return html
  }

  // Loading / empty / error overlay

  showStatus(name) {
    this.status = name
    if (!this.hasStatusTarget) return

    this.statusTarget.hidden = !name
    this.statusTarget.querySelectorAll("[data-status]").forEach(element => {
      element.hidden = element.dataset.status !== name
    })
  }

  // Favourites

  isFilteringFavourites() {
    return new URL(window.location.href).searchParams.get("favourites") === "true"
  }

  getFavourites() {
    try {
      const stored = localStorage.getItem(`favourites-${this.weekSlugValue}`)
      return stored ? JSON.parse(stored) : []
    } catch (e) {
      return []
    }
  }
}

// On between the start and end of its hours today, as the server works them out
function isLive(event, now) {
  return Boolean(event.live_from) && Date.parse(event.live_from) <= now && now < Date.parse(event.live_until)
}

function withoutOutliers(groups) {
  const center = [median(groups.map(group => group.lngLat[0])), median(groups.map(group => group.lngLat[1]))]
  const nearby = groups.filter(group => distanceKm(group.lngLat, center) <= OUTLIER_DISTANCE_KM)
  if (nearby.length === 0) return groups

  const outliers = groups.filter(group => !nearby.includes(group))
  if (outliers.length > 0) {
    console.warn("Map: these events are far from the rest, check their coordinates:",
      outliers.flatMap(group => group.events.map(event => `${event.title} (${event.lat}, ${event.lng})`)))
  }
  return nearby
}

function median(values) {
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

// Great-circle distance between two [lng, lat] points
function distanceKm([lng1, lat1], [lng2, lat2]) {
  const toRadians = Math.PI / 180
  const dLat = (lat2 - lat1) * toRadians
  const dLng = (lng2 - lng1) * toRadians
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toRadians) * Math.cos(lat2 * toRadians) * Math.sin(dLng / 2) ** 2
  return 12742 * Math.asin(Math.sqrt(a))
}

// Events at the same venue share one pin so none are hidden underneath another
function groupByLocation(events) {
  const groups = new Map()

  events.forEach(event => {
    const lat = parseFloat(event.lat)
    const lng = parseFloat(event.lng)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`
    if (!groups.has(key)) groups.set(key, { lngLat: [lng, lat], events: [] })
    groups.get(key).events.push(event)
  })

  return Array.from(groups.values()).map(group => ({
    ...group,
    title: group.events[0].venue_name || group.events[0].neighborhood || group.events[0].title
  }))
}

class FitEventsControl {
  constructor(onClick) {
    this.onClick = onClick
  }

  onAdd() {
    this.container = document.createElement("div")
    this.container.className = "maplibregl-ctrl maplibregl-ctrl-group"
    this.container.innerHTML = `
      <button type="button" class="map-fit-button" title="Show all events" aria-label="Show all events">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
        </svg>
      </button>
    `
    this.container.querySelector("button").addEventListener("click", this.onClick)
    return this.container
  }

  onRemove() {
    this.container.remove()
  }
}
